import type { Breadcrumb, ErrorEvent, Event } from "@sentry/nextjs";

import { isProduction } from "@/env";
import { isAllowedParam, sanitizeSearch, sanitizeUrl } from "@/lib/analytics/url-allowlist";

// Stack-frame `function` names that mark wallet-injected setInterval /
// setTimeout callbacks. Use this when the error message itself is too generic
// to filter via Sentry's `ignoreErrors`. Sourced from Sentry events captured
// for issue DECIBEL-WEB-1WD.
const INJECTED_FRAME_FUNCTIONS: ReadonlySet<string> = new Set(["window.handleWalletConnect2"]);

export function isWalletInjectedEvent(event: ErrorEvent): boolean {
  const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
  return frames.some(
    (f) => typeof f.function === "string" && INJECTED_FRAME_FUNCTIONS.has(f.function),
  );
}

const ENV = { isProduction };
const URL_DATA_KEYS = ["from", "to", "url"] as const;
// URLs embedded in free text, e.g. span descriptions like "GET https://host/path?x=1".
const EMBEDDED_URL = /(?:https?:\/\/[^\s"'<>]+|(?<=^|\s)\/[^\s"'<>]*)/g;

function scrubText(text: string): string {
  return text.replace(EMBEDDED_URL, (match) => sanitizeUrl(match, ENV));
}

function scrubUrlData<T extends Record<string, unknown> | undefined>(data: T): T {
  if (!data) return data;
  const out: Record<string, unknown> = { ...data };
  for (const key of URL_DATA_KEYS) {
    const value = out[key];
    if (typeof value === "string") out[key] = sanitizeUrl(value, ENV);
  }
  const query = out["http.query"];
  if (typeof query === "string") out["http.query"] = sanitizeSearch(query, ENV);
  return out as T;
}

type QueryString = NonNullable<NonNullable<Event["request"]>["query_string"]>;

function scrubQueryString(query: QueryString): QueryString {
  if (typeof query === "string") return sanitizeSearch(query, ENV).replace(/^\?/, "");
  if (Array.isArray(query)) return query.filter(([key]) => isAllowedParam(key, ENV));
  return Object.fromEntries(Object.entries(query).filter(([key]) => isAllowedParam(key, ENV)));
}

export function scrubBreadcrumbUrls(crumb: Breadcrumb): Breadcrumb {
  return crumb.data ? { ...crumb, data: scrubUrlData(crumb.data) } : crumb;
}

const REFERER_HEADERS: Record<string, true> = { referer: true, referrer: true };

function scrubHeaders(headers: Record<string, string>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(headers).map(([key, value]) => [
      key,
      REFERER_HEADERS[key.toLowerCase()] === true && typeof value === "string"
        ? sanitizeUrl(value, ENV)
        : value,
    ]),
  );
}

export function scrubEventUrls<T extends Event>(event: T): T {
  const out: T = { ...event };
  if (out.request) {
    const request = { ...out.request };
    if (request.url) request.url = sanitizeUrl(request.url, ENV);
    if (request.headers) request.headers = scrubHeaders(request.headers);
    if (request.query_string) request.query_string = scrubQueryString(request.query_string);
    out.request = request;
  }
  if (out.transaction) out.transaction = scrubText(out.transaction);
  if (out.spans) {
    out.spans = out.spans.map((span) => ({
      ...span,
      description: span.description ? scrubText(span.description) : span.description,
      data: scrubUrlData(span.data),
    }));
  }
  if (out.breadcrumbs) out.breadcrumbs = out.breadcrumbs.map(scrubBreadcrumbUrls);
  return out;
}

const SENSITIVE_EXTRA_KEYS: Record<string, true> = {
  accesstoken: true,
  authorization: true,
  bearer: true,
  idtoken: true,
  refreshtoken: true,
  token: true,
};

const MAX_EXTRA_DEPTH = 10;

function isSensitiveExtraKey(key: string): boolean {
  const normalized = key.replaceAll(/[^a-z0-9]/gi, "").toLowerCase();
  return SENSITIVE_EXTRA_KEYS[normalized] === true;
}

function scrubExtraValue(value: unknown, depth: number, ancestors: WeakSet<object>): unknown {
  if (typeof value !== "object" || value === null) return value;
  if (depth >= MAX_EXTRA_DEPTH) return "[Truncated]";
  if (ancestors.has(value)) return "[Circular]";
  ancestors.add(value);
  try {
    if (Array.isArray(value)) {
      return value.map((item) => scrubExtraValue(item, depth + 1, ancestors));
    }
    const proto: unknown = Object.getPrototypeOf(value);
    if (proto !== Object.prototype && proto !== null) return value;
    return Object.fromEntries(
      Object.entries(value)
        .filter(([key]) => !isSensitiveExtraKey(key))
        .map(([key, item]) => [key, scrubExtraValue(item, depth + 1, ancestors)]),
    );
  } finally {
    ancestors.delete(value);
  }
}

export function scrubEvent<T extends Event>(event: T): T {
  const out = scrubEventUrls(event);
  if (!out.extra) return out;
  out.extra = scrubExtraValue(out.extra, 0, new WeakSet()) as Record<string, unknown>;
  return out;
}

export const sentryUrlScrubbingHooks = {
  beforeSend: scrubEvent,
  beforeSendTransaction: scrubEvent,
  beforeBreadcrumb: scrubBreadcrumbUrls,
};
